// tableEx — flat tabular grid, powered by @tanstack/react-table.

import { useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import type { DataRow } from "../types/pbi";
import "./_table.css";
// AGENT-T: per-visual CSS — PBI-style headers, zebra rows, sticky chrome.
import "./TableExVisual.css";

// AGENT-T: local sort state — null → asc → desc → null on header click.
type SortDir = "asc" | "desc";
interface SortState {
  key: string;
  dir: SortDir;
}

// AGENT-T: column is treated as numeric if every non-null value in the
// first scan is typeof "number". Falls back to row[0] when only one row
// is available. Used to right-align cells + apply tabular-nums.
function isNumericColumn(rows: DataRow[], key: string): boolean {
  for (const r of rows) {
    const v = r?.[key];
    if (v === null || v === undefined) continue;
    return typeof v === "number";
  }
  return false;
}

// AGENT-T: stable comparator — preserves original index order for ties so
// `null → asc → desc → null` round-trips cleanly back to the source order.
function sortRowsStable(
  rows: DataRow[],
  sort: SortState | null,
): DataRow[] {
  if (!sort) return rows;
  const indexed = rows.map((r, i) => ({ r, i }));
  indexed.sort((a, b) => {
    const av = a.r?.[sort.key];
    const bv = b.r?.[sort.key];
    // nulls always sort to the bottom regardless of direction
    if (av === null || av === undefined) {
      if (bv === null || bv === undefined) return a.i - b.i;
      return 1;
    }
    if (bv === null || bv === undefined) return -1;
    let cmp = 0;
    if (typeof av === "number" && typeof bv === "number") {
      cmp = av - bv;
    } else {
      cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    }
    if (cmp === 0) return a.i - b.i;
    return sort.dir === "asc" ? cmp : -cmp;
  });
  return indexed.map((x) => x.r);
}

export function TableExVisual({ spec, data }: VisualProps) {
  const rows: DataRow[] = data.rows ?? [];

  // AGENT-T: sort state lives in the visual; cycles null → asc → desc → null.
  const [sort, setSort] = useState<SortState | null>(null);

  // AGENT-T: detect numeric columns once per data refresh.
  const numericByKey = useMemo<Record<string, boolean>>(() => {
    const keys = data.columns ?? (rows[0] ? Object.keys(rows[0]) : []);
    const out: Record<string, boolean> = {};
    for (const k of keys) out[k] = isNumericColumn(rows, k);
    return out;
  }, [data.columns, rows]);

  // AGENT-T: sort a stable copy before handing it to react-table.
  const sortedRows = useMemo(
    () => sortRowsStable(rows, sort),
    [rows, sort],
  );

  const columns = useMemo<ColumnDef<DataRow>[]>(() => {
    const keys = data.columns ?? (rows[0] ? Object.keys(rows[0]) : []);
    return keys.map((k) => ({
      accessorKey: k,
      header: k,
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined) return "";
        return String(v);
      },
    }));
  }, [data.columns, rows]);

  const table = useReactTable({
    data: sortedRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // AGENT-T: header click handler — three-state cycle per column.
  const onHeaderClick = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const isEmpty = rows.length === 0;

  return (
    <ChartShell
      title={spec.title}
      isEmpty={isEmpty}
      status={data.status}
    >
      {/* AGENT-T: host wrapper carries the .tableex token + per-visual CSS. */}
      <div className="tableex">
        {isEmpty ? (
          // AGENT-T: centered empty-state placeholder.
          <div className="tableex-empty">No data</div>
        ) : (
          <div className="tableex-scroll">
            <table className="tableex-table">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h, colIdx) => {
                      const key = h.column.id;
                      const isActive = sort?.key === key;
                      const dir: SortDir | null = isActive ? sort!.dir : null;
                      // AGENT-T: per-header class composition — first
                      // column gets sticky-left, numerics right-align, the
                      // active sort column gets sort-asc/sort-desc.
                      const cls = [
                        "tableex-th",
                        colIdx === 0 ? "first" : "",
                        numericByKey[key] ? "numeric" : "",
                        dir === "asc" ? "sort-asc" : "",
                        dir === "desc" ? "sort-desc" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <th
                          key={h.id}
                          className={cls}
                          // AGENT-T: click cycles sort state.
                          onClick={() => onHeaderClick(key)}
                        >
                          {h.isPlaceholder
                            ? null
                            : flexRender(
                                h.column.columnDef.header,
                                h.getContext(),
                              )}
                          {/* AGENT-T: tiny arrow indicator on the active
                              sort column — Unicode ↑/↓. */}
                          <span className="tableex-sort" aria-hidden="true">
                            {dir === "asc"
                              ? "↑"
                              : dir === "desc"
                              ? "↓"
                              : ""}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((r) => (
                  <tr key={r.id} className="tableex-tr">
                    {r.getVisibleCells().map((c, colIdx) => {
                      const key = c.column.id;
                      // AGENT-T: per-cell class composition mirrors header.
                      const cls = [
                        "tableex-td",
                        colIdx === 0 ? "first" : "",
                        numericByKey[key] ? "numeric" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <td key={c.id} className={cls}>
                          {flexRender(
                            c.column.columnDef.cell,
                            c.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ChartShell>
  );
}
